import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import AdminShell from "@/components/AdminShell";
import api from "@/lib/api";

export default function AdminFinance() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api
            .get("/admin/payments")
            .then((r) => setRows(r.data))
            .finally(() => setLoading(false));
    }, []);

    const totalPaid = rows.filter((r) => r.status === "paid").reduce((sum, r) => sum + r.amount, 0) / 100;
    const totalPending = rows.filter((r) => r.status !== "paid").reduce((sum, r) => sum + r.amount, 0) / 100;
    const sources = rows.reduce((acc, r) => {
        if (r.status === "paid") acc[r.description] = (acc[r.description] || 0) + r.amount / 100;
        return acc;
    }, {});

    return (
        <AdminShell title="Finance">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4 mb-10">
                <div className="card-surface p-6" data-testid="fin-total-paid">
                    <div className="eyebrow mb-3">Total Collected</div>
                    <div className="font-mono text-3xl text-saffron">₹{totalPaid.toLocaleString("en-IN")}</div>
                </div>
                <div className="card-surface p-6" data-testid="fin-total-pending">
                    <div className="eyebrow mb-3">Pending</div>
                    <div className="font-mono text-3xl text-white">₹{totalPending.toLocaleString("en-IN")}</div>
                </div>
                <div className="card-surface p-6" data-testid="fin-transactions">
                    <div className="eyebrow mb-3">Transactions</div>
                    <div className="font-mono text-3xl text-white">{rows.length}</div>
                </div>
            </div>

            <div className="card-surface p-6 mb-10">
                <div className="eyebrow mb-4">Revenue by Source</div>
                <div className="space-y-3">
                    {Object.entries(sources).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between text-sm">
                            <span className="text-white/75">{k}</span>
                            <span className="font-mono text-saffron">₹{v.toLocaleString("en-IN")}</span>
                        </div>
                    ))}
                    {Object.keys(sources).length === 0 && <div className="text-white/45 text-sm">No payments yet.</div>}
                </div>
            </div>

            <div className="card-surface overflow-hidden">
                <div className="grid grid-cols-12 px-6 py-3 text-xs uppercase tracking-widest text-white/45 border-b border-white/8">
                    <div className="col-span-3">Order ID</div>
                    <div className="col-span-2">Tracking</div>
                    <div className="col-span-3">Description</div>
                    <div className="col-span-2">Amount</div>
                    <div className="col-span-2 text-right">Status</div>
                </div>
                {loading ? (
                    <div className="p-10 grid place-items-center">
                        <Loader2 className="animate-spin text-saffron" size={20} />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center text-white/45 text-sm">No transactions yet.</div>
                ) : (
                    rows.map((r) => (
                        <div key={r.razorpay_order_id} className="grid grid-cols-12 px-6 py-4 text-sm border-b border-white/5">
                            <div className="col-span-3 font-mono text-xs text-white/75 truncate">{r.razorpay_order_id}</div>
                            <div className="col-span-2 font-mono text-saffron text-xs">{r.tracking_id}</div>
                            <div className="col-span-3 text-white/75">{r.description}</div>
                            <div className="col-span-2 font-mono">₹{(r.amount / 100).toLocaleString("en-IN")}</div>
                            <div className="col-span-2 text-right">
                                <span className={`tag ${r.status === "paid" ? "is-active" : ""}`}>{r.status}</span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </AdminShell>
    );
}
